import React from 'react'
import { Box, Typography, Avatar } from '@mui/material'
import { motion } from 'framer-motion'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'

const podiumColors = ['#FFD700', '#C0C0C0', '#CD7F32']
const podiumGlow = ['rgba(255,215,0,0.5)', 'rgba(192,192,192,0.4)', 'rgba(205,127,50,0.4)']

export default function PodiumScreen({ winners = [], title = 'Félicitations !' }) {
  const ordered = [winners[1], winners[0], winners[2]].filter(Boolean)

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d001a 0%, #1a0035 55%, #2a0050 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
      color: 'white',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Particules décoratives */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: i % 3 === 0 ? 6 : i % 3 === 1 ? 4 : 3,
            height: i % 3 === 0 ? 6 : i % 3 === 1 ? 4 : 3,
            borderRadius: '50%',
            background: i % 2 === 0 ? '#c77dff' : '#f72585',
            left: `${8 + i * 7.5}%`,
            top: `${10 + (i % 4) * 20}%`,
            opacity: 0.4
          }}
          animate={{ y: [-10, 10, -10], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Titre */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, type: 'spring' }}>
        <Typography variant="h2" fontWeight="900" gutterBottom textAlign="center" sx={{
          background: 'linear-gradient(135deg, #c77dff 0%, #ffffff 40%, #f72585 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          letterSpacing: -1, mb: 0
        }}>
          🏆 {title}
        </Typography>
        <Typography variant="h6" textAlign="center" sx={{ color: 'rgba(199,125,255,0.7)', mt: 0.5, mb: 4 }}>
          Compétition terminée — Résultats finaux
        </Typography>
      </motion.div>

      {/* Podium */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 2, md: 4 }, mt: 2 }}>
        {ordered.map((winner, i) => {
          const rank = winner?.rank || (i === 0 ? 2 : i === 1 ? 1 : 3)
          const height = rank === 1 ? 200 : rank === 2 ? 150 : 120
          const size = rank === 1 ? 100 : 80

          return (
            <motion.div
              key={winner?.participantId || i}
              initial={{ y: 120, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: i * 0.35 + 0.5, type: 'spring', stiffness: 80 }}
            >
              <Box sx={{ textAlign: 'center' }}>
                {rank === 1 && (
                  <motion.div
                    animate={{ rotate: [-5, 5, -5] }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  >
                    <Typography fontSize="2.5rem" mb={0.5}>👑</Typography>
                  </motion.div>
                )}

                <Avatar
                  src={winner?.photoUrl}
                  sx={{
                    width: size, height: size, mx: 'auto', mb: 1,
                    border: `4px solid ${podiumColors[rank - 1]}`,
                    boxShadow: `0 0 30px ${podiumGlow[rank - 1]}, 0 0 60px ${podiumGlow[rank - 1]}`
                  }}
                >
                  <EmojiEventsIcon sx={{ fontSize: size * 0.5 }} />
                </Avatar>

                <Typography variant={rank === 1 ? 'h6' : 'body1'} fontWeight="bold" sx={{ maxWidth: 130 }}>
                  {winner?.nickname}
                </Typography>
                <Typography variant="body2" sx={{ color: podiumColors[rank - 1], fontWeight: 'bold', fontSize: rank === 1 ? '1.1rem' : '0.9rem' }}>
                  {winner?.score} pts
                </Typography>

                <Box sx={{
                  width: rank === 1 ? 140 : 110,
                  height,
                  background: rank === 1
                    ? 'linear-gradient(180deg, #FFD700, #FFA500)'
                    : rank === 2
                      ? 'linear-gradient(180deg, #C0C0C0, #A0A0A0)'
                      : 'linear-gradient(180deg, #CD7F32, #A0522D)',
                  borderRadius: '10px 10px 0 0',
                  mt: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 -4px 30px ${podiumGlow[rank - 1]}`
                }}>
                  <Typography variant="h3" fontWeight="900" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                    {rank}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          )
        })}
      </Box>

      {/* Signature */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        style={{ position: 'absolute', bottom: 20, width: '100%', textAlign: 'center' }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.6)', letterSpacing: 0.5 }}>
          Cette plateforme a été fièrement construite par{' '}
          <a href="https://www.nouratech.sn" target="_blank" rel="noopener noreferrer"
            style={{ color: '#c77dff', fontWeight: 'bold', textDecoration: 'none' }}>
            Noura Tech
          </a>
        </Typography>
      </motion.div>
    </Box>
  )
}
